import { DefectResolutionPolicySettings } from './defectResolution';

export enum PolicyTimeTrigger {
  DELIVERY_DATE = 'DELIVERY_DATE',
  ORDER_DATE = 'ORDER_DATE',
  FULFILLMENT_DATE = 'FULFILLMENT_DATE',
}

export enum PolicyStrictnessMode {
  STRICT = 'strict',
  SEMI_AUTOMATIC = 'semi-automatic',
  CUSTOMER_FIRST = 'customer-first',
}

export enum ReturnApprovalMode {
  AUTO_APPROVE = 'AUTO_APPROVE',
  MANUAL_REVIEW = 'MANUAL_REVIEW',
  AUTO_DENY = 'AUTO_DENY',
}

export type RequestApprovalMode = 'AUTOMATIC' | 'MANUAL';
export interface RequestApprovalSettings {
  refund: RequestApprovalMode;
  exchange: RequestApprovalMode;
}

export const getRequestApprovalSettings = (settings?: Partial<PolicySettings> | null): RequestApprovalSettings | null => {
  if (settings && Object.prototype.hasOwnProperty.call(settings, 'approval')) {
    const value = settings.approval;
    return value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 2 &&
      Object.prototype.hasOwnProperty.call(value, 'refund') && Object.prototype.hasOwnProperty.call(value, 'exchange') &&
      [value.refund, value.exchange].every(mode => mode === 'AUTOMATIC' || mode === 'MANUAL')
      ? {refund: value.refund, exchange: value.exchange} : null;
  }
  const legacy = settings as any;
  const strictness = [legacy?.strictness, legacy?.policyStrictness, legacy?.policy_strictness].find(value => value && typeof value === 'object' && !Array.isArray(value)) || {};
  const token = (value: unknown) => typeof value === 'string' ? value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_') : '';
  const eligible = token(strictness.eligibleApprovalMode || strictness.eligible_approval_mode);
  const mode = token(strictness.mode || strictness.policyMode || strictness.policy_mode);
  const automatic = ['AUTO_APPROVE', 'CUSTOMER_FIRST'].includes(eligible) || (!['AUTO_DENY', 'MANUAL_REVIEW', 'MERCHANT_APPROVAL', 'MERCHANT_REVIEW'].includes(eligible) && mode === 'CUSTOMER_FIRST');
  return {refund: automatic ? 'AUTOMATIC' : 'MANUAL', exchange: 'AUTOMATIC'};
};

export type ShipmentDeadlineStartsAt = 'APPROVAL' | 'LABEL_READY';

export type RefundTimingTrigger =
  | 'APPROVAL'
  | 'FIRST_CARRIER_SCAN'
  | 'INBOUND_DELIVERED'
  | 'WAREHOUSE_RECEIVED';

export type SettlementTimingTrigger = RefundTimingTrigger | 'INSPECTION_APPROVED';

export type RefundTimingAppliesTo = 'REFUND' | 'STORE_CREDIT';

export interface SettlementTimingRule {
  trigger: SettlementTimingTrigger;
  delayDays: number;
}

export interface SettlementTimingRules {
  refund: SettlementTimingRule;
  storeCredit: SettlementTimingRule;
  inspectionRequired?: SettlementTimingBranch;
  inspectionNotRequired?: SettlementTimingBranch;
}

export interface SettlementTimingBranch {
  refund: SettlementTimingRule;
  storeCredit: SettlementTimingRule;
}

export interface ReturnInspectionPolicy {
  enabled: boolean;
  mode: 'NEVER' | 'ALWAYS' | 'MATCHING';
  rules: {
    products: { enabled: boolean; productIds: string[]; collectionIds: string[]; tags: string[] };
    itemQuantity: { enabled: boolean; minimum: number };
    returnValue: { enabled: boolean; minimum: number };
    reasons: { enabled: boolean; reasonIds: string[] };
    customerHistory: { enabled: boolean; minimumReturns: number; lookbackDays: number };
    risk: { enabled: boolean; minimumScore: number };
  };
}

export const POLICY_MODE_LABELS: Record<PolicyStrictnessMode, string> = {
  [PolicyStrictnessMode.STRICT]: 'Strict',
  [PolicyStrictnessMode.SEMI_AUTOMATIC]: 'Semi-automatic',
  [PolicyStrictnessMode.CUSTOMER_FIRST]: 'Customer-first',
};

export interface PolicyModeBehavior {
  eligibleApprovalMode: ReturnApprovalMode;
  flaggedApprovalMode: ReturnApprovalMode;
  cardDescription: string;
  merchantSummary: string;
  eligibleOutcomeLabel: string;
  eligibleOutcomeDescription: string;
  flaggedOutcomeLabel: string;
  flaggedOutcomeDescription: string;
  bestForLabel: string;
  bestForDescription: string;
}

export interface ApprovalRules {
  maxReturnValue: number;
  maxItemsInReturn: number;
  maxPriorReturns: number;
  reviewReasonIds: string[];
  requireEvidenceForAutoApproval: boolean;
  reviewUnprotectedOrders: boolean;
}

const POLICY_MODE_RULE_PRESETS: Record<PolicyStrictnessMode, ApprovalRules> = {
  [PolicyStrictnessMode.STRICT]: {
    maxReturnValue: 50,
    maxItemsInReturn: 1,
    maxPriorReturns: 1,
    reviewReasonIds: ['WRONG_ITEM', 'DAMAGED', 'MISSING_ITEM_OR_PARTS'],
    requireEvidenceForAutoApproval: true,
    reviewUnprotectedOrders: true,
  },
  [PolicyStrictnessMode.SEMI_AUTOMATIC]: {
    maxReturnValue: 100,
    maxItemsInReturn: 3,
    maxPriorReturns: 3,
    reviewReasonIds: ['WRONG_ITEM', 'DAMAGED', 'MISSING_ITEM_OR_PARTS'],
    requireEvidenceForAutoApproval: true,
    reviewUnprotectedOrders: false,
  },
  [PolicyStrictnessMode.CUSTOMER_FIRST]: {
    maxReturnValue: 250,
    maxItemsInReturn: 5,
    maxPriorReturns: 5,
    reviewReasonIds: ['DAMAGED'],
    requireEvidenceForAutoApproval: false,
    reviewUnprotectedOrders: false,
  },
};

export const POLICY_MODE_BEHAVIORS: Record<
  PolicyStrictnessMode,
  PolicyModeBehavior
> = {
  [PolicyStrictnessMode.STRICT]: {
    eligibleApprovalMode: ReturnApprovalMode.MANUAL_REVIEW,
    flaggedApprovalMode: ReturnApprovalMode.MANUAL_REVIEW,
    cardDescription: 'Review every eligible request.',
    merchantSummary:
      'Use Strict when you want every eligible return to wait for a team decision.',
    eligibleOutcomeLabel: 'Every eligible request enters manual review',
    eligibleOutcomeDescription:
      'The system applies the return window and exclusions first, then sends eligible requests to your team for approval.',
    flaggedOutcomeLabel: 'Eligible flagged requests stay in review',
    flaggedOutcomeDescription:
      'Ineligible requests are declined, while eligible requests with review flags stay with your team for a decision.',
    bestForLabel: 'High-control review queue',
    bestForDescription:
      'Best for high-risk catalogs, exception-heavy programs, or teams that want to inspect every request themselves.',
  },
  [PolicyStrictnessMode.SEMI_AUTOMATIC]: {
    eligibleApprovalMode: ReturnApprovalMode.MANUAL_REVIEW,
    flaggedApprovalMode: ReturnApprovalMode.MANUAL_REVIEW,
    cardDescription: 'Screen requests, then review them.',
    merchantSummary:
      'Use Semi-Automatic when you want policy rules to screen requests, but your team should still approve each eligible return.',
    eligibleOutcomeLabel: 'Eligible returns queue for approval',
    eligibleOutcomeDescription:
      'The system applies the return window and exclusions first, then sends eligible requests to your team for a final decision.',
    flaggedOutcomeLabel: 'Flagged requests stay in review',
    flaggedOutcomeDescription:
      'Eligible requests with review flags remain with your team; ineligible requests are declined.',
    bestForLabel: 'Balanced control',
    bestForDescription:
      'Best for teams that want faster screening without giving up the final approval step.',
  },
  [PolicyStrictnessMode.CUSTOMER_FIRST]: {
    eligibleApprovalMode: ReturnApprovalMode.AUTO_APPROVE,
    flaggedApprovalMode: ReturnApprovalMode.MANUAL_REVIEW,
    cardDescription: 'Auto-approve eligible requests.',
    merchantSummary:
      'Use Customer-First when you want the fastest shopper experience and only want risky or ineligible returns routed to your team.',
    eligibleOutcomeLabel: 'Eligible returns auto-approve',
    eligibleOutcomeDescription:
      'The system applies the return window and exclusions first, then instantly approves returns that pass without a risk flag.',
    flaggedOutcomeLabel: 'Flagged requests are held for review',
    flaggedOutcomeDescription:
      'Eligible requests with review flags are routed to your team; ineligible requests are declined.',
    bestForLabel: 'Fastest shopper experience',
    bestForDescription:
      'Best for low-friction return programs where most eligible requests should resolve without manual work.',
  },
};

export const getPolicyModeBehavior = (
  mode: PolicyStrictnessMode | undefined | null,
) => {
  return POLICY_MODE_BEHAVIORS[mode || PolicyStrictnessMode.CUSTOMER_FIRST];
};

export const getPolicyModeApprovalPreset = (
  mode: PolicyStrictnessMode | undefined | null,
) => {
  const behavior = getPolicyModeBehavior(mode);

  return {
    eligibleApprovalMode: behavior.eligibleApprovalMode,
    flaggedApprovalMode: behavior.flaggedApprovalMode,
  };
};

export const getPolicyModeRulePreset = (
  mode: PolicyStrictnessMode | undefined | null,
): ApprovalRules => {
  const preset =
    POLICY_MODE_RULE_PRESETS[mode || PolicyStrictnessMode.CUSTOMER_FIRST];

  return {
    ...preset,
    reviewReasonIds: [...preset.reviewReasonIds],
  };
};

export const isPolicyApprovalPresetCustomized = (
  mode: PolicyStrictnessMode | undefined | null,
  eligibleApprovalMode: ReturnApprovalMode,
  flaggedApprovalMode: ReturnApprovalMode,
) => {
  const preset = getPolicyModeApprovalPreset(mode);

  return (
    preset.eligibleApprovalMode !== eligibleApprovalMode ||
    preset.flaggedApprovalMode !== flaggedApprovalMode
  );
};

export interface CarrierServiceSelection {
  carrierId: string;
  services: string[];
}

export interface OwnCarrierAccount {
  id: string;
  provider: string;
  accountNumber: string;
  apiKey?: string;
  meterNumber?: string;
  label?: string;
}

export interface ReturnLocation {
  id: string; // Unique ID for keying
  label?: string; // Optional label (e.g. "West Coast Warehouse")
  street: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  country_alpha2: string;
  phone?: string;
  email?: string;
  isDefault?: boolean;
  source?: 'SHOPIFY' | 'MANUAL';
  shopifyLocationId?: string;
}

export type DatedProductRule = {
  id: string;
  name: string;
  enabled: boolean;
  field: 'PRODUCT_TAG' | 'SKU' | 'PRODUCT_NAME';
  operator: 'EQUALS' | 'CONTAINS';
  value: string;
  purchaseScope?: 'ALL_PURCHASES' | 'DATE_RANGE';
  startDate: string;
  endDate: string;
  timeZone: string;
};

export interface PolicySettings {
  approval?: RequestApprovalSettings;
  defectResolutionPolicy?: DefectResolutionPolicySettings;
  returnPolicyLinks?: {
    defaultUrl?: string;
    byStorefrontHost?: Record<string, string>;
  };
  returns?: {
    enabled?: boolean;
  };
  returnWindow?: {
    days: number;
    trigger: PolicyTimeTrigger;
    deliveryBufferDays?: number;
    holidayExtension?: boolean;
    allowUndelivered?: boolean;
  };
  shipmentDeadline?: {
    days: number;
    startsAt: ShipmentDeadlineStartsAt;
    reminderDaysBeforeDeadline: number[];
    expireAutomatically: boolean;
  };
  refundTiming?: {
    trigger: RefundTimingTrigger;
    delayDays: number;
    appliesTo: RefundTimingAppliesTo[];
    skipAutomationWhenFlagged: boolean;
  };
  settlementTiming?: SettlementTimingRules;
  fraudRules?: {
    maxItemsInReturn?: number;
    maxReturnValue?: number;
    maxOrderValue?: number;
    maxPriorReturns?: number;
    maxPriorClaims?: number;
    requireEvidenceForAutoApproval?: boolean;
    reviewUnprotectedOrders?: boolean;
    yofiHighRiskRequiresReview?: boolean;
  };
  reasonRules?: {
    damagedRequiresReview: boolean;
    damagedRequiresPhotos?: boolean;
    damagedNotifiesMerchant?: boolean;
    reviewReasonIds?: string[];
    requireEvidenceForAutoApproval?: boolean;
    reviewUnprotectedOrders?: boolean;
  };
  eligibility?: {
    productRulesVersion?: number;
    productRulesEnabled?: boolean;
    enabledAt?: string | null;
    finalSaleEnforced?: boolean;
    excludedTags?: string[];
    excludedCollections?: string[];
    excludedSkus?: string[];
    datedRules?: DatedProductRule[];
    excludedCollectionLabels?: Record<string, string>;
    discountThreshold?: number;
  };
  strictness?: {
    mode: PolicyStrictnessMode;
    eligibleApprovalMode?: ReturnApprovalMode;
    flaggedApprovalMode?: ReturnApprovalMode;
    eligibilityPreScreening?: boolean;
  };
  operationalControls?: {
    inspection?: { enabled: boolean; mode?: ReturnInspectionPolicy['mode']; rules?: ReturnInspectionPolicy['rules'] };
    warehouse?: {};
    logistics?: {
      autoGenerateLabels: boolean;
      // Legacy fields (kept for backward compatibility during migration)
      useShipaidRates?: boolean;
      useOwnCarrier?: boolean;
      selectedCourierId?: string;
      carrierProvider?: string;
      accountNumber?: string;
      apiKey?: string;

      // New fields
      shipAidRatesEnabled?: boolean;
      ownRatesEnabled?: boolean;

      // Selected ShipAid carriers and their services
      shipAidCarriers?: CarrierServiceSelection[];

      // Connected own carrier accounts (Legacy)
      ownCarrierAccounts?: OwnCarrierAccount[];

      // Selected Own carriers and their services (New Flow)
      ownCarriers?: CarrierServiceSelection[];

      // Return Locations (Multiple)
      returnLocations?: ReturnLocation[];

      // Legacy single address (kept for backward compatibility)
      returnAddress: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        zip: string;
        country?: string;
        country_alpha2?: string;
        phone?: string;
        email?: string;
      };
      preferredService?: string;
    };
    permissions?: {
      cxTeamOverrides: boolean;
      overrideVIP: boolean;
      overrideDamaged: boolean;
      overrideWindowExceeded: boolean;
    };
    abuseGuardrails?: {
      maxReturnsPerMonth: number;
      flagHighVelocity: boolean;
    };
    customerExperience?: {
      requireCustomerNotes?: boolean;
      requireReturnPhotos?: boolean;
      evidenceRequirements?: {
        defaults?: {
          notes?: 'REASON_BASED' | 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
          photos?: 'REASON_BASED' | 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
        };
        byReason?: Record<
          string,
          {
            notes?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
            photos?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
          }
        >;
      };
      returnInstructions?: string;
    };
  };
}

const nonNegativeNumberOrFallback = (value: unknown, fallback: number) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const getApprovalRules = (
  settings: Partial<PolicySettings> | null | undefined,
  mode: PolicyStrictnessMode | undefined | null,
): ApprovalRules => {
  const preset = getPolicyModeRulePreset(mode);
  const fraudRules: Partial<NonNullable<PolicySettings['fraudRules']>> =
    settings?.fraudRules || {};
  const reasonRules: Partial<NonNullable<PolicySettings['reasonRules']>> =
    settings?.reasonRules || {};
  const storedReasonIds = Array.isArray(reasonRules.reviewReasonIds)
    ? reasonRules.reviewReasonIds.filter(
        (reasonId): reasonId is string =>
          typeof reasonId === 'string' && reasonId.trim().length > 0,
      )
    : null;

  return {
    maxReturnValue: nonNegativeNumberOrFallback(
      fraudRules.maxReturnValue ?? fraudRules.maxOrderValue,
      preset.maxReturnValue,
    ),
    maxItemsInReturn: nonNegativeNumberOrFallback(
      fraudRules.maxItemsInReturn,
      preset.maxItemsInReturn,
    ),
    maxPriorReturns: nonNegativeNumberOrFallback(
      fraudRules.maxPriorReturns,
      preset.maxPriorReturns,
    ),
    reviewReasonIds:
      storedReasonIds ??
      (reasonRules.damagedRequiresReview === true
        ? ['DAMAGED']
        : [...preset.reviewReasonIds]),
    requireEvidenceForAutoApproval:
      fraudRules.requireEvidenceForAutoApproval ??
      reasonRules.requireEvidenceForAutoApproval ??
      preset.requireEvidenceForAutoApproval,
    reviewUnprotectedOrders:
      fraudRules.reviewUnprotectedOrders ??
      reasonRules.reviewUnprotectedOrders ??
      preset.reviewUnprotectedOrders,
  };
};

export const isPolicyRulePresetCustomized = (
  mode: PolicyStrictnessMode | undefined | null,
  rules: ApprovalRules,
) => {
  const preset = getPolicyModeRulePreset(mode);
  return (
    preset.maxReturnValue !== rules.maxReturnValue ||
    preset.maxItemsInReturn !== rules.maxItemsInReturn ||
    preset.maxPriorReturns !== rules.maxPriorReturns ||
    preset.requireEvidenceForAutoApproval !==
      rules.requireEvidenceForAutoApproval ||
    preset.reviewUnprotectedOrders !== rules.reviewUnprotectedOrders ||
    preset.reviewReasonIds.length !== rules.reviewReasonIds.length ||
    preset.reviewReasonIds.some(
      (reasonId) => !rules.reviewReasonIds.includes(reasonId),
    )
  );
};

const REFUND_TIMING_TRIGGERS: RefundTimingTrigger[] = [
  'APPROVAL',
  'FIRST_CARRIER_SCAN',
  'INBOUND_DELIVERED',
  'WAREHOUSE_RECEIVED',
];

const normalizeSettlementTimingRule = (
  value: Partial<SettlementTimingRule> | undefined,
  fallback: SettlementTimingRule,
  allowInspection = false,
): SettlementTimingRule => ({
  trigger: (allowInspection && value?.trigger === 'INSPECTION_APPROVED') || REFUND_TIMING_TRIGGERS.includes(
    value?.trigger as RefundTimingTrigger,
  )
    ? (value?.trigger as SettlementTimingTrigger)
    : fallback.trigger,
  delayDays:
    Number.isInteger(value?.delayDays) && Number(value?.delayDays) >= 0
      ? Number(value?.delayDays)
      : fallback.delayDays,
});

export const getSettlementTimingRules = (
  settings?: Partial<PolicySettings> | null,
): SettlementTimingRules => {
  const legacyRule: SettlementTimingRule = {
    trigger: REFUND_TIMING_TRIGGERS.includes(
      settings?.refundTiming?.trigger as RefundTimingTrigger,
    )
      ? (settings?.refundTiming?.trigger as RefundTimingTrigger)
      : 'WAREHOUSE_RECEIVED',
    delayDays:
      Number.isInteger(settings?.refundTiming?.delayDays) &&
      Number(settings?.refundTiming?.delayDays) >= 0
        ? Number(settings?.refundTiming?.delayDays)
        : 0,
  };

  return {
    refund: normalizeSettlementTimingRule(
      settings?.settlementTiming?.refund,
      legacyRule,
    ),
    storeCredit: normalizeSettlementTimingRule(
      settings?.settlementTiming?.storeCredit,
      legacyRule,
    ),
    ...(settings?.settlementTiming?.inspectionRequired ? { inspectionRequired: {
      refund: normalizeSettlementTimingRule(settings.settlementTiming.inspectionRequired.refund, { trigger: 'INSPECTION_APPROVED', delayDays: 0 }, true),
      storeCredit: normalizeSettlementTimingRule(settings.settlementTiming.inspectionRequired.storeCredit, { trigger: 'INSPECTION_APPROVED', delayDays: 0 }, true),
    } } : {}),
    ...(settings?.settlementTiming?.inspectionNotRequired ? { inspectionNotRequired: {
      refund: normalizeSettlementTimingRule(settings.settlementTiming.inspectionNotRequired.refund, legacyRule),
      storeCredit: normalizeSettlementTimingRule(settings.settlementTiming.inspectionNotRequired.storeCredit, legacyRule),
    } } : {}),
  };
};
